import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import StoreBadges from "../components/StoreBadges";
import Waveform from "../components/Waveform";
import ChannelStrip from "../components/ChannelStrip";
import HeroMontage from "../components/HeroMontage";
import { Reveal, MaskLine } from "../components/Reveal";

const CONNECT = [
  {
    title: "Playlists",
    copy: "Songs, photos, and videos. Capture how the music felt in the moment.",
  },
  {
    title: "Sessions",
    copy: "Listen together in real time. Same song, same moment, from anywhere.",
    featured: true,
  },
  {
    title: "Music DM's",
    copy: "Share music, playlists, and files all in one place.",
  },
  {
    title: "Profile",
    copy: "Follow friends, family, artists, and music lovers around the world.",
  },
];

function Hero() {
  const scrollToContent = () => {
    const target = document.getElementById("ways-section");
    if (!target) return;
    if (window.__lenis) window.__lenis.scrollTo(target, { offset: -64 });
    else target.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="hero-cine relative flex min-h-[88svh] flex-col overflow-hidden bg-[linear-gradient(180deg,#050508_0%,#050508_80%,transparent_100%)]"
      data-testid="hero"
    >
      <HeroMontage />
      <div className="relative flex flex-1 flex-col items-center justify-center px-5 pb-24 pt-28 text-center sm:px-8">
        <h1 className="hero-title" data-testid="hero-title">
          <MaskLine delay={0.05}>Music.</MaskLine>
          <MaskLine delay={0.18}>
            <span className="text-gradient">Memories.</span>
          </MaskLine>
          <MaskLine delay={0.31}>Moments.</MaskLine>
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10"
        >
          <StoreBadges testidPrefix="hero-badge" />
        </motion.div>
      </div>
      <button
        type="button"
        onClick={scrollToContent}
        className="hero-scroll-cue"
        data-testid="hero-scroll-cue"
        aria-label="Scroll to explore"
      >
        <ArrowDown size={24} strokeWidth={1.5} />
      </button>
    </section>
  );
}

function WaysToConnect() {
  return (
    <section className="section !pt-6" id="ways-section" data-testid="ways-section">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal className="flex flex-col items-center text-center">
          <Waveform bars={44} className="h-14 max-w-2xl" testid="ways-waveform" />
          <h2 className="section-title mt-3" data-testid="ways-title">
            Ways to <span className="text-gradient">Connect</span>
          </h2>
          <p className="body-lg mt-4" data-testid="ways-sub">Share the sound. Feel the moment together.</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {CONNECT.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.07} testid={`connect-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <ChannelStrip title={c.title} copy={c.copy} featured={c.featured} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="section !pt-4 pb-24" data-testid="final-cta-section">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal className="text-center">
          <h2 className="section-title" data-testid="final-cta-title">
            Ready to <span className="text-gradient">Tap In?</span>
          </h2>
          <p className="body-lg mt-4" data-testid="final-cta-sub">
            Your music community starts here. Share, discover, and connect through music.
          </p>
          <div className="mt-9">
            <StoreBadges testidPrefix="final-badge" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <WaysToConnect />
      <FinalCta />
    </>
  );
}